import { computed, effect, inject, Injectable, linkedSignal, signal } from "@angular/core";
import {
  AccessLevel,
  AccountMarketplace,
  AccountType,
  DashboardConfig,
  DashboardConfigApi,
  DashboardType,
  Marketplace,
} from "@front/m19-api-client";
import { catchAjaxError } from "@front/m19-utils";
import { untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, map, Observable, of, ReplaySubject, switchMap, take, tap } from "rxjs";

import { toSignal } from "@angular/core/rxjs-interop";
import { OrganizationAccountGroups } from "@front/m19-models";
import { AccountSelectionService } from "@front/m19-services";
import { ModalService, ToasterService } from "@front/m19-ui";
import { TranslocoService } from "@jsverse/transloco";
import { DASHBOARD_TYPE_360 } from "@m19-board/360-common/360-injection-tokens";
import {
  Settings360AccountSelectionModalComponent,
  Settings360AccountSelectionModalData,
} from "@m19-board/360-common/base-360-settings/settings-360-account-selection-modal/settings-360-account-selection-modal.component";
import { getLastSelectedDashboardKey } from "@m19-board/guards/dashboard360-last-selected-guard/dashboard360-last-selected.guard";
import { TableWidgetComponent } from "@m19-board/widget/table-widget/table-widget.component";
import { DashboardConfigEx, DashboardSubConfig } from "@m19-board/widget/widget-builder.service";
import {
  BrandConfig,
  hasProductTags,
  isBrandConfig,
  isProductGroupConfig,
  KPIWidgetConfig,
  ProductGroupConfig,
  ProductTagFilterConfig,
  WidgetConfig,
} from "@m19-board/widget/widget-config";
import { ViewBy, WidgetType } from "@m19-board/widget/widget.model";
import { Utils } from "gridstack";
import { Router } from "@angular/router";

type Organization_DashboardType = string;
type DashboardConfigId = number;
export type DashboardConfigs<T extends WidgetType> = Map<DashboardConfigId, DashboardConfigEx<T>>;

function organization_dashboardType(organizationId: number, dashboardType: DashboardType) {
  return `${organizationId}_${dashboardType}`;
}

export type DashboardScope = {
  accountType?: AccountType;
  accountId?: string;
  marketplace?: Marketplace;
};

@Injectable()
export class DashboardConfigService<T extends WidgetType> {
  private readonly toastrService = inject(ToasterService);
  private readonly dashboardConfigApi = inject(DashboardConfigApi);
  private readonly translocoService = inject(TranslocoService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly DASHBOARD_TYPE = inject(DASHBOARD_TYPE_360);
  private readonly dashboardConfigs$ = new Map<Organization_DashboardType, ReplaySubject<DashboardConfigs<T>>>();
  private readonly dashboardConfigs = new Map<Organization_DashboardType, DashboardConfigs<T>>();
  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  public static readonly DASHBOARD_NAME_MAX_LENGTH = 80;
  private static readonly DASHBOARD_GRID_OUTBOUND = 12;

  public readonly allDashboardConfigs$: Observable<DashboardConfigs<T>> =
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace: AccountMarketplace) =>
        combineLatest([
          this.getDashboardConfig(accountMarketplace.resourceOrganizationId!, this.DASHBOARD_TYPE, AccountType.SELLER),
          this.getDashboardConfig(accountMarketplace.resourceOrganizationId!, this.DASHBOARD_TYPE, AccountType.VENDOR),
        ]),
      ),
      map(([configSeller, configVendor]) => new Map([...configSeller.entries(), ...configVendor.entries()])),
    );

  public readonly availableDashboardConfigs$: Observable<DashboardConfigs<T>> =
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace: AccountMarketplace) =>
        this.allDashboardConfigs$.pipe(
          map((dashboardConfigs) => this.filterConfigs(dashboardConfigs, accountMarketplace)),
        ),
      ),
    );

  public readonly unvailableDashboardConfigs$: Observable<DashboardConfigs<T>> =
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((accountMarketplace: AccountMarketplace) =>
        this.allDashboardConfigs$.pipe(
          map((dashboardConfigs) => this.filterConfigs(dashboardConfigs, accountMarketplace, false)),
        ),
      ),
    );

  public _savedDashboardConfig = signal<DashboardConfigEx<T> | undefined>(undefined);
  public readonly savedDashboardConfig = this._savedDashboardConfig.asReadonly();

  public _editingDashboardConfig = linkedSignal(() => this._savedDashboardConfig(), {
    equal: this.equalDashboardConfig,
  });
  public readonly editingDashboardConfig = this._editingDashboardConfig.asReadonly();
  public readonly hasLocalConfigToSave = computed(() => {
    return (
      this.savedDashboardConfig() &&
      this.editingDashboardConfig() &&
      !this.equalDashboardConfig(this.savedDashboardConfig(), this.editingDashboardConfig())
    );
  });
  public readonly isSavingDashboard = signal(false);
  public readonly isDiscardingDashboard = signal(false);

  constructor() {
    effect(() => {
      const currentDashboard = this.savedDashboardConfig();
      if (!currentDashboard) return;
      const lastSelectedDashboardStorageKey = getLastSelectedDashboardKey(
        currentDashboard.accountType!,
        this.DASHBOARD_TYPE,
      );
      localStorage.setItem(lastSelectedDashboardStorageKey, currentDashboard.dashboardId!.toString());
    });
  }

  public updateWidgetTitle(widgetId: string, title: string) {
    this._editingDashboardConfig.update((dashboardConfig) => {
      if (!dashboardConfig) return dashboardConfig;
      const newWidgetConfig = dashboardConfig.widgetConfig.map((w) => (w.id === widgetId ? { ...w, title } : w));
      return { ...dashboardConfig, widgetConfig: newWidgetConfig };
    });
  }

  public updateEditingDashboardConfig(newConfig: DashboardConfigEx<T>) {
    this._editingDashboardConfig.set(newConfig);
  }

  public updateLocalWidgetConfig(newWidgetConfig: WidgetConfig<T>) {
    this._editingDashboardConfig.update((dashboardConfig) => {
      if (!dashboardConfig) return dashboardConfig;
      return {
        ...dashboardConfig,
        widgetConfig: dashboardConfig.widgetConfig.map((w) => (w.id === newWidgetConfig.id ? newWidgetConfig : w)),
      };
    });
  }

  public updateLocalWidgetConfigs(newWidgetConfig: WidgetConfig<T>[]) {
    this._editingDashboardConfig.update((dashboardConfig) => {
      if (!dashboardConfig) return dashboardConfig;
      return { ...dashboardConfig, widgetConfig: newWidgetConfig };
    });
  }

  public setSavedDashboardConfig(dashboardConfig: DashboardConfigEx<T> | undefined) {
    this._savedDashboardConfig.set(dashboardConfig);
  }

  public discardEditingDashboardConfig() {
    this.isDiscardingDashboard.set(true);
    // force asynchronous execution has _editingDashboardConfig and dependent signals are numerous
    // see: https://github.com/m19-dev/main-repo/issues/11011
    setTimeout(() => {
      this._editingDashboardConfig.set({ ...this._savedDashboardConfig()! });
      this.isDiscardingDashboard.set(false);
    }, 0);
  }

  public getDashboardConfig(
    organizationId: number,
    dashboardType: DashboardType,
    accountType: AccountType,
  ): Observable<DashboardConfigs<T>> {
    const key = organization_dashboardType(organizationId, dashboardType);
    if (this.dashboardConfigs$.has(key)) {
      return this.dashboardConfigs$.get(key)!.pipe(
        map((dashboardConfigs) => {
          const filteredDashboardConfigs = new Map<number, DashboardConfigEx<T>>();
          for (const [key, value] of dashboardConfigs.entries()) {
            if (value.accountType === accountType) {
              filteredDashboardConfigs.set(key, value);
            }
          }
          return filteredDashboardConfigs;
        }),
      );
    }
    const subject = new ReplaySubject<DashboardConfigs<T>>(1);
    this.dashboardConfigs$.set(key, subject);
    this.dashboardConfigApi
      .listDashboardConfigs({ organizationId, dashboardType })
      .pipe(catchAjaxError())
      .subscribe({
        next: (response) => {
          const dashboardConfigs = new Map<DashboardConfigId, DashboardConfigEx<T>>();
          for (const dashboardConfig of response) {
            const dashboardConfigEx = this.buildDashboardConfig(dashboardConfig);
            dashboardConfigs.set(dashboardConfig.dashboardId!, dashboardConfigEx);
          }
          this.dashboardConfigs.set(key, dashboardConfigs);
          subject.next(dashboardConfigs);
        },
        error: (e) => subject.error(e),
      });
    return subject.pipe(
      map((dashboardConfigs) => {
        const filteredDashboardConfigs = new Map<number, DashboardConfigEx<T>>();
        for (const [key, value] of dashboardConfigs.entries()) {
          if (value.accountType === accountType) {
            filteredDashboardConfigs.set(key, value);
          }
        }
        return filteredDashboardConfigs;
      }),
    );
  }

  public updateDashboardName(organizationId: number, dashboardId: number, name: string) {
    return this.updateDashboardConfig(organizationId, dashboardId, {
      ...this.editingDashboardConfig()!,
      name,
    }).subscribe({
      next: () => {
        this.toastrService.success(
          this.translocoService.translate("account360-dashboard-settings.name_update_success"),
        );
      },
      error: (error) => {
        this.toastrService.error(error.message);
      },
    });
  }

  hasAccountSpecificConfig(widgetConfigs: WidgetConfig<T>[]): boolean {
    return widgetConfigs.some(
      (w) => isProductGroupConfig(w) || (isBrandConfig(w) && w.brandIds && w.brandIds.length > 0) || hasProductTags(w),
    );
  }

  clearAccountSpecificConfig<T extends WidgetType>(dashboardConfig: DashboardConfigEx<T>) {
    dashboardConfig.widgetConfig = dashboardConfig.widgetConfig.map((config) => {
      const newConfig = { ...config } as WidgetConfig<T> & ProductTagFilterConfig & ProductGroupConfig & BrandConfig;
      newConfig.customField1 = undefined;
      newConfig.customField2 = undefined;
      newConfig.productGroupId = undefined;
      newConfig.brandIds = undefined;

      return newConfig;
    });
    return dashboardConfig;
  }

  configureDashboardScope<S extends DashboardScope>(dashboardScope: S | undefined, widgetConfigs: WidgetConfig<T>[]) {
    const hasSpecifiedDashboardScope = !!dashboardScope;
    const hasDashboardScope = !!dashboardScope?.accountId && !!dashboardScope?.marketplace;
    const hasAccountSpecificConfig = this.hasAccountSpecificConfig(widgetConfigs);

    const newScope: DashboardScope = dashboardScope ? { ...dashboardScope } : {};
    if (!hasDashboardScope && hasAccountSpecificConfig) {
      newScope.accountId = this.accountMarketplace()?.accountId;
      newScope.marketplace = this.accountMarketplace()?.marketplace;
    } else if (hasSpecifiedDashboardScope && !hasAccountSpecificConfig && !hasDashboardScope) {
      newScope.accountId = undefined;
      newScope.marketplace = undefined;
    }
    return newScope;
  }

  createDashboardConfig(
    organizationId: number,
    dashboardScope: DashboardScope,
    dashboardName: string,
    widgetConfig: WidgetConfig<T>[],
  ): Observable<DashboardConfig> {
    return this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      take(1),
      map((accountMarketplace) => {
        const hasOnlyWriteAccess = accountMarketplace.accessLevel === AccessLevel.WRITE;

        const newDashboardScope = dashboardScope;
        if (hasOnlyWriteAccess && !newDashboardScope.accountId) {
          newDashboardScope.marketplace = accountMarketplace.marketplace;
          newDashboardScope.accountId = accountMarketplace.accountId;
        }
        return newDashboardScope;
      }),
      switchMap((scope: DashboardScope) =>
        this.dashboardConfigApi.createDashboardConfig({
          dashboardConfig: {
            organizationId,
            dashboardType: this.DASHBOARD_TYPE,
            accountType: scope.accountType,
            accountId: scope.accountId,
            marketplace: scope.marketplace,
            name: dashboardName,
            config: JSON.stringify({ widgetConfig }),
            configVersion: 1,
          },
        }),
      ),
      catchAjaxError(),
      map((response) => response.entity!),
      tap((dashboardConfig) => {
        const key = organization_dashboardType(organizationId, this.DASHBOARD_TYPE);
        if (!this.dashboardConfigs$.has(key)) {
          return;
        }
        this.dashboardConfigs.get(key)!.set(dashboardConfig.dashboardId!, this.buildDashboardConfig(dashboardConfig));
        this.dashboardConfigs$.get(key)!.next(this.dashboardConfigs.get(key)!);
      }),
    );
  }

  updateDashboardConfigWithGlobalFilters(filters: ProductTagFilterConfig) {
    const globalConfig: ProductTagFilterConfig = {};
    if (filters.customField1) {
      globalConfig.customField1 = filters.customField1;
    }
    if (filters.customField2) {
      globalConfig.customField2 = filters.customField2;
    }
    this._editingDashboardConfig.update((dashboardConfig) => {
      if (!dashboardConfig) return dashboardConfig;
      return { ...dashboardConfig, globalFilters: globalConfig };
    });
  }

  updateDashboardConfig(
    organizationId: number,
    dashboardId: number | undefined,
    newDashboardConfig: DashboardConfigEx<T>,
    newWidgetConfig?: WidgetConfig<T>[],
    newDashboardScope?: DashboardScope,
    refreshDashboards = true,
  ) {
    newDashboardConfig.widgetConfig = newWidgetConfig ?? newDashboardConfig.widgetConfig;

    const scope = this.configureDashboardScope(newDashboardScope, newDashboardConfig.widgetConfig);
    newDashboardConfig = { ...newDashboardConfig, ...scope };

    if (!this.isConfigCoordinatesValid(newDashboardConfig)) {
      throw new Error("Invalid dashboard configuration");
    }

    return this.dashboardConfigApi
      .updateDashboardConfig({
        dashboardConfig: {
          dashboardId,
          organizationId,
          name: newDashboardConfig.name,
          dashboardType: newDashboardConfig.dashboardType,
          accountType: newDashboardConfig.accountType,
          accountId: newDashboardConfig.accountId,
          marketplace: newDashboardConfig.marketplace,
          configVersion: 1,
          config: this.stringifySubConfig({
            widgetConfig: newDashboardConfig.widgetConfig,
            globalFilters: newDashboardConfig.globalFilters,
          }),
        },
      })
      .pipe(
        catchAjaxError(),
        map((response) => {
          const dashboardConfig = response.entity as DashboardConfig;
          if (!refreshDashboards) return dashboardConfig;

          const key = organization_dashboardType(organizationId, dashboardConfig.dashboardType!);
          if (this.dashboardConfigs$.has(key)) {
            this.dashboardConfigs
              .get(key)!
              .set(dashboardConfig.dashboardId!, this.buildDashboardConfig(dashboardConfig));
            this.dashboardConfigs$.get(key)!.next(this.dashboardConfigs.get(key)!);
          }
          return dashboardConfig;
        }),
      );
  }

  updateGlobalFiltersToConfig(globalFilters: ProductTagFilterConfig): void {
    const newConfig: WidgetConfig<T>[] = [];
    for (const widgetConfig of this.editingDashboardConfig()?.widgetConfig ?? []) {
      const widget = this.handleWidgetGlobalFilters(widgetConfig, globalFilters);
      newConfig.push(widget);
    }

    this.updateEditingDashboardConfig({
      ...this.editingDashboardConfig(),
      widgetConfig: newConfig,
      globalFilters: globalFilters,
    });
  }

  deleteDashboardConfig(organizationId: number, dashboardType: DashboardType, dashboardId: number) {
    return this.dashboardConfigApi
      .deleteDashboardConfig({
        dashboardId,
      })
      .pipe(
        catchAjaxError(),
        tap(() => {
          const key = organization_dashboardType(organizationId, dashboardType);
          if (!this.dashboardConfigs$.has(key)) {
            return;
          }
          this.dashboardConfigs.get(key)!.delete(dashboardId);
          this.dashboardConfigs$.get(key)!.next(this.dashboardConfigs.get(key)!);
        }),
      );
  }

  saveDashboardOnMetricColumnChanged(
    widgetComponent: TableWidgetComponent,
    config: WidgetConfig<T>,
    dashboardConfig: DashboardConfigEx<T>,
    accessLevel: AccessLevel,
    accountMarketplace: AccountMarketplace,
  ) {
    if (accessLevel === AccessLevel.ADMIN) {
      widgetComponent.metricColumnChange
        .pipe(
          untilDestroyed(widgetComponent),
          switchMap((metrics) => {
            const newConfig = {
              ...config,
              metricColumns: metrics.map((m) => ({
                id: m.metric.id,
                hide: m.hide,
              })),
            };

            dashboardConfig.widgetConfig = dashboardConfig.widgetConfig.map((c) =>
              c.id === config.id ? newConfig : c,
            );

            return (
              this.updateDashboardConfig(
                accountMarketplace.resourceOrganizationId!,
                dashboardConfig.dashboardId,
                dashboardConfig,
              ) ?? of(undefined)
            );
          }),
        )
        .subscribe({
          error: (err) => {
            this.toastrService.error(
              err,
              this.translocoService.translate("account360-dashboard-view.error_saving_dashboard"),
            );
          },
        });
    }
  }

  generateWidgetId() {
    return crypto.randomUUID();
  }

  openDashboardEditAccountModal(
    dashboardConfig: DashboardConfigEx<T>,
    allAccountGroups: OrganizationAccountGroups[],
    isAuthorizedUserWriteAccess?: boolean,
  ): Observable<DashboardScope | null> {
    if (this.hasAccountSpecificConfig(dashboardConfig.widgetConfig)) return of(null);
    const dashboard = dashboardConfig;

    const dashboardAccountId = dashboard.accountId;
    const dashboardMarketplace = dashboard.marketplace;
    const dashboardOrganizationId = dashboard.organizationId;
    const dashboardType = dashboard.accountType;

    const dashboardScope: DashboardScope = {
      accountId: dashboardAccountId,
      marketplace: dashboardMarketplace,
    };

    const dashboardAccountGroup = allAccountGroups
      ?.flatMap((group) => group.accountGroups)
      .find((accountGroup) =>
        accountGroup
          .getAccountMarketplaces()
          .some((am) => am.accountId === dashboardAccountId && am.marketplace === dashboardMarketplace),
      );

    const modalRef = this.modalService.openModal<Settings360AccountSelectionModalData, DashboardScope>(
      Settings360AccountSelectionModalComponent,
      {
        modalTitle: this.translocoService.translate("account360-dashboard-settings.update_dashboard_account"),
        data: {
          dashboardOrganizationId: dashboardOrganizationId,
          dashboardAccountGroup: dashboardAccountGroup,
          dashboardAccountMarketplace: dashboardScope,
          dashboardAccountType: dashboardType,
          isAuthorizedUserWriteAccess: isAuthorizedUserWriteAccess,
        },
        position: "center-top",
      },
    );

    return modalRef.afterClosed();
  }

  getAccountMarketplaceToSelect(
    selectedAccountMarketplace: AccountMarketplace,
    accountMarketplaces: AccountMarketplace[],
    dashboardScope: DashboardScope,
  ): AccountMarketplace {
    let accMarketplaceToSelect: AccountMarketplace | undefined;
    if (dashboardScope.accountId) {
      accMarketplaceToSelect = accountMarketplaces?.find(
        (am) =>
          am.accountId === dashboardScope.accountId &&
          am.marketplace === dashboardScope.marketplace &&
          am.resourceOrganizationId === selectedAccountMarketplace.resourceOrganizationId,
      );
    }
    // If accessing to a global dashboard from a different type of dashboard
    // we need to select the account marketplace of the same type
    else if (selectedAccountMarketplace.accountType !== dashboardScope.accountType) {
      accMarketplaceToSelect = accountMarketplaces?.find(
        (am) =>
          am.accountType === dashboardScope.accountType &&
          am.resourceOrganizationId === selectedAccountMarketplace.resourceOrganizationId,
      );
    }

    return accMarketplaceToSelect ?? selectedAccountMarketplace;
  }

  redirectToDashboard(
    redirectUrl: string,
    dashboardConfig: DashboardConfig,
    accountMarketplace: AccountMarketplace,
    accountMarketplaces: AccountMarketplace[],
    scope: DashboardScope,
  ) {
    const accMarketplaceToSelect = this.getAccountMarketplaceToSelect(
      accountMarketplace,
      accountMarketplaces ?? [],
      scope,
    );
    this.accountSelectionService.selectAccountMarketplace([accMarketplaceToSelect]);
    const queryParams = {
      accountId: accMarketplaceToSelect.accountId,
      marketplace: accMarketplaceToSelect.marketplace,
      orgId: accMarketplaceToSelect.resourceOrganizationId,
    };

    this.router.navigate([redirectUrl, dashboardConfig.dashboardId], {
      queryParamsHandling: "replace",
      queryParams,
    });
  }

  private buildDashboardConfig(dashboardConfig: DashboardConfig): DashboardConfigEx<T> {
    try {
      const subConfig: DashboardSubConfig<T> = JSON.parse(dashboardConfig.config ?? "");
      const widgetConfig = subConfig.widgetConfig;
      if (!widgetConfig) {
        return {
          ...dashboardConfig,
          widgetConfig: [],
          globalFilters: {},
          error: "Issue loading dashboard configuration because of invalid format.",
        };
      }
      // Migration for old KPIs that didn't have a viewBy
      for (const widget of widgetConfig) {
        if (widget.type === WidgetType.WeeklyStats && !(widget as unknown as KPIWidgetConfig).viewBy) {
          (widget as unknown as KPIWidgetConfig).viewBy = ViewBy.GLOBAL;
        }
      }
      return {
        ...dashboardConfig,
        widgetConfig: widgetConfig,
        globalFilters: subConfig.globalFilters,
      };
    } catch (ex) {
      // invalid JSON
      return {
        ...dashboardConfig,
        widgetConfig: [],
        globalFilters: {},
        error: "Issue loading dashboard configuration because of invalid format.",
      };
    }
  }

  private filterConfigs(
    dashboardConfigs: DashboardConfigs<T>,
    accountMarketplace: AccountMarketplace,
    onlyAvailableForAccount: boolean = true,
  ) {
    const filteredDashboardConfigs = new Map<number, DashboardConfigEx<T>>();
    for (const [key, value] of dashboardConfigs.entries()) {
      if (
        (!onlyAvailableForAccount && !this.isConfigAvailableForAccount(value, accountMarketplace)) ||
        (onlyAvailableForAccount && this.isConfigAvailableForAccount(value, accountMarketplace))
      ) {
        filteredDashboardConfigs.set(key, value);
      }
    }
    return filteredDashboardConfigs;
  }

  private isConfigAvailableForAccount(dashboardConfig: DashboardConfigEx<T>, accountMarketplace: AccountMarketplace) {
    return (
      dashboardConfig.accountType === accountMarketplace.accountType &&
      (!dashboardConfig.accountId ||
        (dashboardConfig.accountId === accountMarketplace.accountId &&
          dashboardConfig.marketplace === accountMarketplace.marketplace))
    );
  }

  private isConfigCoordinatesValid(dashboardConfig: DashboardConfigEx<T>): boolean {
    for (let i = 0; i < dashboardConfig.widgetConfig.length; i++) {
      for (let j = 0; j < dashboardConfig.widgetConfig.length; j++) {
        const widgetConfig1 = this.normalizeWidgetConfig(dashboardConfig.widgetConfig[i]);
        const widgetConfig2 = this.normalizeWidgetConfig(dashboardConfig.widgetConfig[j]);
        if (widgetConfig1.id === widgetConfig2.id) {
          continue;
        }
        if (
          !this.isWidgetConfigCoordinatesValid(widgetConfig1) ||
          !this.isWidgetConfigCoordinatesValid(widgetConfig2)
        ) {
          return false;
        }
        if (Utils.isIntercepted(widgetConfig1, widgetConfig2)) {
          return false;
        }
      }
    }
    return true;
  }

  private normalizeWidgetConfig(widgetConfig: WidgetConfig<T>): WidgetConfig<T> {
    return { ...widgetConfig, h: widgetConfig.h ?? 1, w: widgetConfig.w ?? 1 };
  }

  private isWidgetConfigCoordinatesValid(widgetConfig: WidgetConfig<T>): boolean {
    const x = widgetConfig.x ?? 0;
    const y = widgetConfig.y ?? 0;
    const w = widgetConfig.w;
    const h = widgetConfig.h;

    if (x < 0 || y < 0 || w <= 0 || h <= 0) {
      return false;
    }
    if (x >= DashboardConfigService.DASHBOARD_GRID_OUTBOUND || w > DashboardConfigService.DASHBOARD_GRID_OUTBOUND) {
      return false;
    }
    return true;
  }

  private equalDashboardConfig(a: DashboardConfigEx<T> | undefined, b: DashboardConfigEx<T> | undefined) {
    if (!a || !b) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  }

  private stringifySubConfig(subConfig: DashboardSubConfig<T>): string {
    return JSON.stringify(subConfig);
  }

  private handleWidgetGlobalFilters(
    widgetConfig: WidgetConfig<T>,
    globalFilters: ProductTagFilterConfig,
  ): WidgetConfig<T> {
    const res = { ...widgetConfig } as WidgetConfig<T> & ProductTagFilterConfig;
    res.customField1 = globalFilters.customField1;
    res.customField2 = globalFilters.customField2;

    return res;
  }
}
